import { useRef, useState, useEffect } from "react";
import iconCamera from "../../assets/icons/camera-icon.svg"
import iconFile from "../../assets/icons/file-icon.svg"
import {
  Container,
  InputContainer,
  Modal,
  VideoBox,
  RecIndicator
} from "./styles";

import sendIcon from "../../assets/icons/task-check-icon.svg";
import attachmentIcon from "../../assets/icons/attachment-icon.svg";
import closeIcon from "../../assets/icons/close-icon.svg";
import cameraIcon from "../../assets/icons/camera-icon.svg";
import cancelIcon from "../../assets/icons/cancel-icon.svg";

type SendVideoProps = {
  setVideoBlob: React.Dispatch<React.SetStateAction<Blob | null>>;
};

export function SendVideo({ setVideoBlob }: SendVideoProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const [fileName, setFileName] = useState("");
  const [finalBlob, setFinalBlob] = useState<Blob | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [timer, setTimer] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const isCancellingRef = useRef(false);

  useEffect(() => {
    setVideoBlob(finalBlob);
  }, [finalBlob, setVideoBlob]);

  useEffect(() => {
    if (!openModal || !isRecording) return;

    if (timer >= 60) {
      mediaRecorderRef.current?.stop();
      setIsRecording(false);
      return;
    }
    
  }, [timer, openModal, isRecording]);

  useEffect(() => {
    if (!openModal) return;

    let stream: MediaStream | null = null;

    const start = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true
        });

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }

        chunksRef.current = [];

        const recorder = new MediaRecorder(stream);
        mediaRecorderRef.current = recorder;

        recorder.ondataavailable = e => {
          if (e.data.size > 0) chunksRef.current.push(e.data);
        };

        recorder.onstop = () => {
          setIsRecording(false);

          if (isCancellingRef.current) {
            isCancellingRef.current = false;
            reset();
            return;
          }

          const blob = new Blob(chunksRef.current, { type: "video/webm" });
          setFileName("gravacao.webm");
          setFinalBlob(blob);
          setVideoBlob(blob);

          stopStream();
          clearVideo();
          setOpenModal(false);
        };
      } catch (err) {
        console.error(err);
        alert("Não foi possível acessar a câmera ou microfone.");
        setOpenModal(false);
      }
    };

    start();

    return () => {
      stream?.getTracks().forEach(t => t.stop());
      clearVideo();
      mediaRecorderRef.current = null;
    };
  }, [openModal]);

  useEffect(() => {
    if (!openModal) return;

    if (timer >= 60) {
      if (mediaRecorderRef.current?.state === "recording") {
        mediaRecorderRef.current.stop();
      }
      return;
    }

    const interval = setInterval(() => {
      setTimer(t => t + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [timer, openModal]);

  function stopStream() {
    const stream = videoRef.current?.srcObject as MediaStream | null;
    stream?.getTracks().forEach(t => t.stop());
  }

  function reset() {
    chunksRef.current = [];
    isCancellingRef.current = false;

    setIsRecording(false);
    setTimer(0);
    setFileName("");
    setFinalBlob(null);
    setVideoBlob(null);

    stopStream();
    clearVideo();
    setOpenModal(false);
  }

  function handleClickSelect() {
    fileInputRef.current?.click();
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) {
      setFileName(file.name);
      setFinalBlob(file);
    }
  }

  function handleOpenRecorder() {
    setOpenModal(true);
  }

  function cancel() {
    isCancellingRef.current = true;

    setIsRecording(false);
    setTimer(0);
    setFileName("");
    setFinalBlob(null);
    setVideoBlob(null);

    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
    } else {
      reset();
    }
  }

  function finishNow() {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
    }
  }

  function downloadRecorded() {
    if (!finalBlob) return;
    const url = URL.createObjectURL(finalBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName || "video.webm";
    a.click();
    URL.revokeObjectURL(url);
  }

  function startRecording() {
    if (!mediaRecorderRef.current) return;

    chunksRef.current = [];
    setIsRecording(true);
    setTimer(0);
    mediaRecorderRef.current.start();
  }

  function clearVideo() {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.srcObject = null;
    }
  }

  return (
    <Container>
      <InputContainer>
        <div tabIndex={7} className="record hint" onClick={handleOpenRecorder} data-hint="Gravar" data-hint-placement="bottom" onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleOpenRecorder();
          }
        }}>
          <img
            src={iconCamera}
            alt="Gravar vídeo"
            data-hint="Gravar"
            data-hint-placement="bottom"

          />
        </div>
        <div tabIndex={8} className="send-file hint" onClick={handleClickSelect} data-hint="Selecionar arquivo" data-hint-placement="bottom" onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClickSelect();
          }
        }}>
          <img
            src={iconFile}
            alt="Selecionar arquivo"
          />
        </div>
      </InputContainer>
      <input
        type="file"
        accept="video/*"
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: "none" }}
      />
      {fileName && (
        <p className="file-name">
          Arquivo selecionado:
          <img src={attachmentIcon} alt="Salvar" />
          <strong style={{ cursor: "pointer" }} onClick={downloadRecorded}>
            {fileName}
          </strong>
          <img src={closeIcon} style={{ cursor: "pointer" }} alt="Fechar" onClick={reset} />
        </p>
      )}
      {openModal && (
        <Modal>
          <VideoBox className="recording">
            <header>Gravação de video</header>
            {isRecording && (
              <RecIndicator>
                <span className="dot" />
                REC {timer}s / 60s
              </RecIndicator>
              
            )}
            <video ref={videoRef} autoPlay muted />
            {!isRecording && (
              <div className="video-buttons">
                <div className="video-button" onClick={cancel} tabIndex={10} onKeyDown={(e) => {if (e.key === "Enter" || e.key === " ") { e.preventDefault(); cancel(); }}}>
                  <img className="cancel" src={cancelIcon} style={{ cursor: "pointer" }} alt="Cancelar"  />
                  <p>Cancelar</p>
                </div>
                <div className="video-button" onClick={startRecording} tabIndex={11} onKeyDown={(e) => {if (e.key === "Enter" || e.key === " ") { e.preventDefault(); startRecording(); }}}>
                  <img className="play" src={cameraIcon} style={{ cursor: "pointer" }} alt="Gravar"  />
                  <p>Gravar</p>
                </div>
              </div>

            )}
            {isRecording && (
              <div className="video-buttons">
                <div className="video-button" onClick={cancel} tabIndex={10} onKeyDown={(e) => {if (e.key === "Enter" || e.key === " ") { e.preventDefault(); cancel(); }}}>
                  <img className="cancel" src={cancelIcon} alt="Cancelar"  />
                  <p>Cancelar</p>
                </div>
                <div className="video-button" onClick={finishNow} tabIndex={11} onKeyDown={(e) => {if (e.key === "Enter" || e.key === " ") { e.preventDefault(); finishNow(); }}}>
                  <img className="send" src={sendIcon} alt="Enviar"  />
                  <p>Enviar</p>
                </div>
              </div>
            )}
          </VideoBox>
        </Modal>
      )}
    </Container>
  );
}
